import { createPortal } from 'react-dom';
import React, { useEffect } from 'react';
import classNames from 'classnames';

import './Modal.scss';

// Modal is appended through a portal to the "#modal" div in the HTML template.
const modalRoot = typeof document !== 'undefined' && document.getElementById('modal');
const el = typeof document !== 'undefined' && document.createElement('div');

export interface ModalProps {
  children: React.ReactNode;
  iframe?: boolean;
  isOpen: boolean;
  autoWrapper?: boolean;
  // function will be fired when clicked outside of the "children", ie on the backdrop
  dismissCallback?(): void;
}

/**
 * Modal works in the "Bring your own content" manner. Meaning you have to provide a fully styled content for it to
 * display something. You can use one of the predefined ones, like the "ModalSimplePanel" component
 */
// Example using route:
// <Switch>
//   <Route path="*/modal">
//     <Modal isOpen dismissCallback={redirectToJob}>
//       <ModalSimplePanel showHead title="Hello world" closeCallback={redirectToJob}>
//         Hello this is some modal
//       </ModalSimplePanel>
//     </Modal>
//   </Route>
// </Switch>
// const modalRoot = typeof document !== 'undefined' && document.getElementById('modal');
// const el = typeof document !== 'undefined' && document.createElement('div');

export const Modal = ({ children, iframe = false, isOpen, autoWrapper, dismissCallback }: ModalProps) => {
  if (typeof document === 'undefined') {
    // tslint:disable-next-line:no-null-keyword
    return null;
  }

  const cleanUp = (element): void => {
    if (!document.body.classList.contains('modal-body--open')) {
      return;
    }
    document.body.classList.remove('modal-body--open');
    modalRoot.removeChild(element);
  };

  useEffect(() => {

    if (isOpen) {
      document.body.classList.add('modal-body--open');
    }
    modalRoot?.appendChild(el);

    return () => cleanUp(el);

  }, [isOpen]);

  return createPortal(
    isOpen &&
    <div styleName={classNames('modal', { 'modal--iframe': iframe })}>
      <div styleName={classNames({ modal__backdrop: !iframe })} onClick={() => dismissCallback && dismissCallback()} />
      <div styleName={classNames('modal__wrapper', { 'modal__wrapper-auto': autoWrapper })}>
        {children}
      </div>
    </div>,
    el
  );
};

export default Modal;
